/**
 * SOURCE OF TRUTH KEYWORDS: ErrorCode, ERROR_CODES, ALL_ERROR_CODES, error codes, error union,
 *   API errors, VALIDATION_ERROR, INTERNAL_ERROR, NOT_FOUND, error envelope, wire contract
 * WHAT:  The single union of every machine-readable error code the API can return.
 * WHY:   Spec §10 fixes the error envelope `{ error: { code, message, details } }`, and the
 *        frontend repository maps codes to UI states. This lives in contracts/ rather than in
 *        core/errors/ deliberately: contracts/ is the innermost, dependency-free layer, so the
 *        union can be consumed by the backend AND published to the frontend without dragging a
 *        framework import behind it (ADR-001).
 * WHERE: Consumed by src/core/errors/error-codes.ts (which adds the HTTP status mapping) and
 *        src/core/errors/domain-error.ts. Published to the frontend via src/contracts/index.ts.
 *
 * Codes are added by the phase that can first return them. Domain codes (SLOT_ALREADY_HELD,
 * TOKEN_REPLAY_DETECTED, …) arrive with their modules — see IMPLEMENTATION_PHASES.md.
 */

export const ERROR_CODES = {
  /** Request body, query or params failed schema validation. HTTP 400. */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  /** Syntactically valid but malformed for this endpoint. HTTP 400. */
  BAD_REQUEST: 'BAD_REQUEST',
  /** No credentials, or credentials that do not authenticate. HTTP 401. */
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  /** Authenticated but not permitted. HTTP 403. */
  FORBIDDEN: 'FORBIDDEN',
  /** Does not exist, or exists in another tenant — deliberately indistinguishable. HTTP 404. */
  NOT_FOUND: 'NOT_FOUND',
  /** Uniqueness or concurrency collision. HTTP 409. */
  CONFLICT: 'CONFLICT',
  /** Gone and will not return — an expired token, a consumed link. HTTP 410. */
  GONE: 'GONE',
  /** Valid input rejected by a business rule. HTTP 422. */
  UNPROCESSABLE: 'UNPROCESSABLE',
  /** Rate limit exceeded. HTTP 429. */
  RATE_LIMITED: 'RATE_LIMITED',
  /** Unexpected failure. The message is deliberately opaque to the client. HTTP 500. */
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  /** A dependency this request needed is unavailable. Fails closed by design. HTTP 503. */
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  /**
   * An EXTERNAL system this organization is layered over could not be reached. HTTP 502.
   *
   * ★ Distinct from SERVICE_UNAVAILABLE, and the distinction matters to the caller. 503 means
   * Recoup's own dependency is down and every tenant is affected; 502 means one organization's
   * third-party calendar is down while the rest of the product works. API_PLAN §4 names this code
   * for `GET /v1/availability` in Layer Mode, where the alternative — an empty 200 — would be
   * indistinguishable from a genuinely full diary.
   */
  INTEGRATION_UNAVAILABLE: 'INTEGRATION_UNAVAILABLE',
  /**
   * A step of the request pipeline exists but is not implemented in this build.
   * Never returned by a released phase — see src/core/chokepoint/not-yet-wired.ts.
   * HTTP 500.
   */
  NOT_YET_WIRED: 'NOT_YET_WIRED',

  // ── Phase 3, authentication (spec §5) ────────────────────────────────────
  //
  // Deliberately granular. The frontend behaves DIFFERENTLY per code — it retries on
  // TOKEN_EXPIRED and hard-logs-out on TOKEN_REPLAY_DETECTED — so collapsing these into
  // UNAUTHENTICATED would make correct client behaviour impossible.

  /**
   * Wrong password, or no such user. HTTP 401.
   *
   * ★ Returned identically for both cases, in status, code, message AND timing (spec §5.1 step 1
   * looks the user up case-insensitively; §5.2 sets the hashing cost). Distinguishing them is a
   * user-enumeration oracle: an attacker learns which addresses hold accounts.
   */
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  /** Credentials were correct but `users.status != 'active'` (spec §5.1 step 3). HTTP 403. */
  USER_NOT_ACTIVE: 'USER_NOT_ACTIVE',
  /** Login attempts exceeded 10 / 15 min per email+IP (spec §5.2). HTTP 429. */
  LOGIN_RATE_LIMITED: 'LOGIN_RATE_LIMITED',

  /** Refresh token absent, malformed, or matching no row. HTTP 401. */
  TOKEN_INVALID: 'TOKEN_INVALID',
  /** Refresh token found but past `expires_at`. The client may re-login. HTTP 401. */
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  /**
   * ★ An already-revoked refresh token was presented (spec §5.1: "replay-attack signal").
   *
   * Every token in that user's family is revoked, an audit row is written and a `critical`
   * notification is raised. Distinct from TOKEN_EXPIRED because the client must hard-log-out
   * rather than retry, and because the two mean opposite things operationally: one is a normal
   * lifecycle event, the other is evidence of a stolen token.
   */
  TOKEN_REPLAY_DETECTED: 'TOKEN_REPLAY_DETECTED',

  /** Invite token matched no user, or its hash did not verify. HTTP 401. */
  INVITE_TOKEN_INVALID: 'INVITE_TOKEN_INVALID',
  /** Invite token was valid but past `invite_expires_at`. HTTP 410. */
  INVITE_EXPIRED: 'INVITE_EXPIRED',
  /** Invite accepted for a user who is already active. HTTP 409. */
  USER_ALREADY_ACTIVE: 'USER_ALREADY_ACTIVE',

  /** Action-token signature failed, or the payload did not decode (spec §5.5). HTTP 401. */
  ACTION_TOKEN_INVALID: 'ACTION_TOKEN_INVALID',
  /** Action token past its `exp`. HTTP 410. */
  ACTION_TOKEN_EXPIRED: 'ACTION_TOKEN_EXPIRED',
  /**
   * Action token already used (spec §5.5: "reject replay even before expiry if already
   * consumed"). HTTP 410.
   */
  ACTION_TOKEN_CONSUMED: 'ACTION_TOKEN_CONSUMED',
  /** Action token is valid but its `action_scope` does not permit this operation. HTTP 403. */
  ACTION_TOKEN_SCOPE_MISMATCH: 'ACTION_TOKEN_SCOPE_MISMATCH',
  /**
   * The offer this action link was issued for is no longer open. HTTP 410.
   *
   * ★ Distinct from ACTION_TOKEN_EXPIRED, and the distinction is what the customer sees. The token
   * may still be perfectly valid — the SLOT is gone, because somebody else took it or the hold
   * lapsed. API_PLAN §9 names this code for exactly that, and the frontend shows "that appointment
   * has been taken" rather than "your link has expired", which would be untrue and unactionable.
   */
  OFFER_EXPIRED: 'OFFER_EXPIRED',

  /**
   * RecoupSlots P8. Creating this would exceed the organization's plan limit (locations, staff or
   * customers). HTTP 422 (API_PLAN §10: business-rule rejection). Nothing existing is affected — reads, updates and deletes still work;
   * the details carry the limit and an upgrade message for the frontend's upgrade prompt.
   */
  PLAN_LIMIT_REACHED: 'PLAN_LIMIT_REACHED',

  /** RecoupSlots P9 (D8). The tenant is read-only until billing is resolved; reads still work. HTTP 422. */
  BILLING_READ_ONLY: 'BILLING_READ_ONLY',
  /** RecoupSlots P9 (D16). The demo tenant is billing-exempt: no checkout or portal. HTTP 422. */
  BILLING_EXEMPT: 'BILLING_EXEMPT',
  /** RecoupSlots P9. A live subscription exists; change plans in the billing portal. HTTP 409. */
  ALREADY_SUBSCRIBED: 'ALREADY_SUBSCRIBED',
  /** RecoupSlots P9. No test-mode Stripe Price is configured for that plan. HTTP 422. */
  PLAN_PRICE_NOT_CONFIGURED: 'PLAN_PRICE_NOT_CONFIGURED',

  /** API key absent, unknown, revoked or expired (spec §5.7). HTTP 401. */
  API_KEY_INVALID: 'API_KEY_INVALID',

  /**
   * Same `Idempotency-Key` replayed with a DIFFERENT request body (spec §9). HTTP 422.
   *
   * Not a 409: replaying the stored response would answer a question the client did not ask.
   */
  /**
   * A booking mutation arrived with no `Idempotency-Key`. HTTP 400.
   *
   * ★ Spec §9 and §14 make the header MANDATORY on state-mutating booking endpoints, and
   * BACKEND_ARCHITECTURE §6 states the consequence plainly: "Missing on a booking endpoint → 400".
   * Accepting the request without one would mean a retried booking could double-book.
   */
  IDEMPOTENCY_KEY_REQUIRED: 'IDEMPOTENCY_KEY_REQUIRED',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /** A request with the same `Idempotency-Key` is still in flight. HTTP 409. */
  IDEMPOTENCY_IN_PROGRESS: 'IDEMPOTENCY_IN_PROGRESS',
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]

/** Every code, for iteration in contract tests. */
export const ALL_ERROR_CODES: readonly ErrorCode[] = Object.values(ERROR_CODES)
